/**
 * Model-specific rendering parameters for Sogni AI image generation
 *
 * Centralized configuration for frontend direct Sogni SDK calls.
 * Each model has optimized parameters based on performance testing.
 */

export const MODEL_CONFIGS = {
  'moss_image_sft_int8_convrot': {
    steps: 50,
    guidance: 3,
    sampler: 'euler',
    scheduler: 'sgm_uniform',
    negativePrompt: ''
  },
  'moss_image_sft_dmd8_int8_convrot': {
    steps: 8,
    guidance: 1,
    sampler: 'euler',
    scheduler: 'sgm_uniform',
    negativePrompt: ''
  },
  'z_image_turbo_bf16': {
    // steps omitted - use model default
    guidance: 1.0,
    negativePrompt: ''
  },
  'krea2_turbo_fp8_scaled': {
    steps: 4,
    guidance: 1.0,
    negativePrompt: '',
    baseTimeout: 60000  // Turbo model, allow a little extra queue time
  },
  'flux1-schnell-fp8': {
    steps: 4,
    guidance: 1.0,
    negativePrompt: ''
  },
  'chroma-v.46-flash_fp8': {
    steps: 12,
    guidance: 1.0,
    // Chroma is tuned for the Beta scheduler (Sogni docs: "Euler – Beta" gives
    // warmer color + sharper micro-contrast). Without this it falls back to the
    // worker default "Simple", which makes flash renders look soft/iffy.
    sampler: 'euler',
    scheduler: 'beta',
    // Aligned to the app.sogni.ai reference negative prompt for Chroma. (At
    // CFG 1.0 flash is single-pass so this has little effect, but kept identical
    // to app.sogni.ai for consistency.)
    negativePrompt: 'malformation, bad anatomy, bad hands, missing fingers, cropped, low quality, bad quality, jpeg artifacts, watermark'
  },
  'coreml-zavychromaxl_v80': {
    steps: 40,
    guidance: 6.0,
    negativePrompt: 'malformation, lowres, bad anatomy, bad hands, error, missing fingers, cropped, worst quality, low quality, normal quality, bad quality, jpeg artifacts, watermark'
  },
  'chroma-v48-detail-svd_fp8': {
    // Matches the Sogni Create (app.sogni.ai) reference for this model exactly:
    // euler + beta + CFG 5 + 26 steps. Verified to render cleanly at every
    // resolution incl. 512px. The overexposure bug was the SAMPLER: dpmpp_2m +
    // beta diverges to blown-out white below ~1MP; euler is stable. CFG and
    // resolution were red herrings (CFG 5 is correct — the live app uses it).
    steps: 26,
    guidance: 5,
    sampler: 'euler',
    scheduler: 'beta',
    // Aligned to the app.sogni.ai reference negative prompt for Chroma.
    negativePrompt: 'malformation, bad anatomy, bad hands, missing fingers, cropped, low quality, bad quality, jpeg artifacts, watermark'
  },
  'coreml-animagineXL40_v4Opt': {
    // steps and guidance omitted - use model defaults
    negativePrompt: 'malformation, bad anatomy, bad hands, missing fingers, cropped, low quality, bad quality, jpeg artifacts, watermark'
  },
  // --- Added to the Sogni network 2026-07-09 (see docs/superpowers/specs/2026-07-10-add-new-sogni-models-design.md) ---
  'chroma1-hd_fp8_scaled': {
    // Chroma family: reuse the verified chroma-v48-detail-svd_fp8 setup. The generic
    // worker default (dpmpp_2m + CFG 7.5) blows out / flattens Chroma; euler + beta +
    // CFG 5 renders cleanly at every resolution.
    steps: 26,
    guidance: 5,
    sampler: 'euler',
    scheduler: 'beta',
    negativePrompt: 'malformation, bad anatomy, bad hands, missing fingers, cropped, low quality, bad quality, jpeg artifacts, watermark'
  },
  'dark_beast_z_image_turbo_v9_bf16': {
    // Z-Image Turbo community fine-tune. Mirrors z_image_turbo_bf16:
    // steps omitted (model default 4), guidance 1.0, no negative.
    guidance: 1.0,
    negativePrompt: ''
  },
  'dark_beast_krea2_fp8': {
    // Numeric controls use the live tier defaults (16 steps, fixed guidance 1).
    negativePrompt: 'malformation, bad anatomy, bad hands, missing fingers, cropped, low quality, bad quality, jpeg artifacts, watermark'
  },
  'one_obsession_v22_fp16': {
    // One Obsession v22 — Illustrious XL anime checkpoint. Network recommendedSettings.
    steps: 20,
    guidance: 7.5,
    negativePrompt: 'malformation, bad anatomy, bad hands, missing fingers, cropped, low quality, bad quality, jpeg artifacts, watermark'
  }
};

/**
 * New models must resolve their settings from the live parameter catalog.
 */
export const DEFAULT_MODEL_CONFIG = {};

/**
 * Get configuration for a specific model
 * Falls back to default config if model not found
 *
 * @param {string} modelId - The model identifier
 * @returns {Object} Model configuration with steps, guidance, and negativePrompt
 */
export function getModelConfig(modelId) {
  return MODEL_CONFIGS[modelId] || DEFAULT_MODEL_CONFIG;
}
